import React from 'react'
import { cva } from 'class-variance-authority'
import { cn } from '~/lib/utils.js'
import * as _ from 'lodash'
import { Typography } from '~/components/_atoms'

export const cardVariants = cva(
  'card block relative @container rounded-2.5xl',
  {
    variants: {
      variant: {
        default: 'border',
        flat: 'border-none',
      },
      color: {
        neutral: 'bg-white border-cod-gray-200',
        primary: 'bg-white border-intoo-blue-dark-600',
        secondary: 'bg-willow-brook-50 border-willow-brook-200',
        select: 'bg-select-blue text-white border-select-blue',
        'select-light': 'bg-select-blue-light border-none',
        'select-secondary': 'bg-intoo-blue-medium-50',
      },
      shadow: { default: 'shadow-md', none: 'shadow-none' },
    },
    defaultVariants: {
      variant: 'default',
      color: 'neutral',
      shadow: 'default',
    },
  },
)

const Card = React.forwardRef(
  (
    {
      actions,
      children,
      className,
      color,
      density,
      icon,
      image,
      imageAlt,
      imageBadge,
      header,
      shadow,
      title,
      titleColor = 'primary',
      variant,
      ...props
    },
    ref,
  ) => {
    if (_.isEmpty(titleColor) && color === 'secondary') titleColor = 'secondary'
    let densityPadding = 6
    if (density === 'compact') densityPadding = 3
    if (density === 'roomy') densityPadding = 10

    return (
      <div
        className={cn(cardVariants({ variant, color, shadow, className }))}
        ref={ref}
        {...props}
      >
        <div
          className={cn(
            'card-ctr grid h-full',
            image ? 'grid-rows-[auto_1fr]' : 'grid-rows-1',
          )}
        >
          {image && (
            <div className="card-img relative -mx-px -mt-px">
              {imageBadge && (
                <div className="absolute bottom-0 right-0 z-10">
                  <div className="h-11 rounded-tl-[99999px] bg-primary py-2 pl-7 pr-5 text-base font-normal text-[#FFFFFF]">
                    {imageBadge}
                  </div>
                </div>
              )}
              <img
                src={image}
                alt={imageAlt || title}
                className="h-60 w-full rounded-tl-2.5xl rounded-tr-2.5xl object-cover"
              />
            </div>
          )}
          <div
            className={cn(
              'card-layout grid gap-y-3',
              densityPadding && `p-${densityPadding}`,
              (header || title) && actions
                ? 'grid-rows-[auto_1fr_auto]'
                : header || title
                  ? 'grid-rows-[auto_1fr]'
                  : actions
                    ? 'grid-rows-[1fr_auto]'
                    : 'grid-rows-1',
            )}
          >
            {(!_.isEmpty(title) || !_.isEmpty(header)) && (
              <div className="card-header flex items-center gap-2">
                {header ? (
                  <>{header}</>
                ) : (
                  <Typography
                    variant="title4"
                    className="flex flex-grow gap-2"
                    color={titleColor}
                  >
                    {title}
                  </Typography>
                )}
                {icon && (
                  <Typography className="h-full" color={titleColor}>
                    {icon}
                  </Typography>
                )}
              </div>
            )}
            <div className="card-content min-w-0">{children}</div>
            {!_.isEmpty(actions) && (
              <div className="card-actions flex items-end justify-end">
                {actions}
              </div>
            )}
          </div>
        </div>
      </div>
    )
  },
)
Card.displayName = 'Card'

export default Card
